import { FC, useCallback, useMemo, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';
import { isBefore, subYears } from 'date-fns';
import * as z from 'zod';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useAuth, ProfileSetupPayload } from '@hooks/Auth';

import InputText from '@components/elements/InputText';
import ButtonMighty from '@components/elements/ButtonMighty';
import DatePicker from '@components/elements/DatePicker';
import Loader from '@components/elements/Loader';

import './SignupPage.css';

const FORM_SCHEMA = z
  .object({
    firstName: z
      .string()
      .nonempty()
      .regex(/^[A-Za-z ]+$/, 'Invalid format'),
    lastName: z
      .string()
      .nonempty()
      .regex(/^[A-Za-z ]+$/, 'Invalid format'),
    gender: z.union([
      z.literal('Default'),
      z.literal('Male'),
      z.literal('Female'),
      z.literal('Other'),
    ]),
    yearOfBirth: z.date(),
  })
  .refine((data) => data.gender !== 'Default', {
    message: 'Gender is required',
    path: ['gender'], // set path of error
  })
  .refine((data) => isBefore(data.yearOfBirth, subYears(new Date(), 16)), {
    message: 'You must be at least 16 years old.',
    path: ['yearOfBirth'], // set path of error
  });

type FormInput = z.infer<typeof FORM_SCHEMA>;

const SignupPage: FC = () => {
  const history = useHistory<any>();

  const { dbUser, setupProfile } = useAuth();

  useEffect(() => {
    if (!dbUser) history.push('/login');
  }, [dbUser, history]);

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    control,
  } = useForm<FormInput>({
    mode: 'onChange',
    resolver: zodResolver(FORM_SCHEMA),
    shouldUnregister: false,
  });

  console.log({ errors });

  const onSubmit = useCallback(
    async (data: FormInput) => {
      try {
        const dbPayload: ProfileSetupPayload = {
          yearOfBirth: data.yearOfBirth.getFullYear(),
          firstName: data.firstName,
          lastName: data.lastName,
          gender: data.gender,
        };
        console.log('calling setUpProfile');

        await setupProfile(dbPayload);

        toast.success('Successfully setup your profile.');
        history.replace('/');
      } catch (err: any) {
        console.error('setUpProfile err', err);
        toast.error(err.message);
      }
    },
    [history, setupProfile]
  );

  const content = useMemo(() => {
    if (isSubmitting)
      return (
        <Loader
          title="Setting up your profile..."
          description="This may take a few seconds, please don't close this page."
        />
      );
    return (
      <form className="signup-form" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <InputText
            placeholder="Your First Name"
            shadowColor="#FFB400"
            borderColor="#00cf49"
            {...register('firstName')}
          />
          {errors.firstName && (
            <p className="text-right text-red">*{errors.firstName.message}</p>
          )}
        </div>

        <div>
          <InputText
            placeholder="Your Last Name"
            shadowColor="#FFB400"
            borderColor="#00cf49"
            {...register('lastName')}
          />
          {errors.lastName && (
            <p className="text-right text-red">*{errors.lastName.message}</p>
          )}
        </div>

        <div className="signup-field-gender">
          <select
            className="am-list-item"
            style={{ width: '100%' }}
            defaultValue="Default"
            {...register('gender')}
          >
            <option value="Default" disabled>
              Select your gender
            </option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Prefer not to say</option>
          </select>
          {errors.gender && (
            <p className="text-right text-red">*{errors.gender.message}</p>
          )}
        </div>

        <div className="signup-field-datepicker">
          <Controller
            name="yearOfBirth"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <DatePicker
                end={subYears(new Date(), 16)}
                start={subYears(new Date(), 100)}
                interval="year"
                title="Select your year of birth"
                placeholder="Year of Birth"
                shadowColor="#FFB400"
                borderColor="#00cf49"
                {...field}
              />
            )}
          />

          {errors.yearOfBirth && (
            <p className="text-right text-red">*{errors.yearOfBirth.message}</p>
          )}
        </div>

        <div className="signup-action-container">
          <ButtonMighty
            type="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
            color="#4400FF"
            text="Create profile"
            borderColor="#ff1dfc"
            margin="0"
            textColor="#fff"
          />
        </div>
      </form>
    );
  }, [
    isSubmitting,
    handleSubmit,
    onSubmit,
    register,
    errors.firstName,
    errors.lastName,
    errors.gender,
    errors.yearOfBirth,
    control,
  ]);
  return (
    <div className="pt-20 setup-page">
      <div className="container mx-auto md:w-108">
        <div className="setup-container">
          <h1 className="mb-8 text-6xl font-bold">Setup Your Profile</h1>
          <div>{content}</div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
